import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { v4 as uuidv4 } from 'uuid';
import { io } from 'socket.io-client';
import styled from 'styled-components';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { ToastContainer, toast } from 'react-toastify';
import { API_URL } from '../../constants/string-constants';
import { signInWithGoogle } from '../../utils/firebase-auth';
import {
  createMessage,
  clearMessages,
  deleteMessage,
  MESSAGE_STATUS_CACHED,
  MESSAGE_STATUS_NEW,
  addReactionToMessage,
  removeReactionFromMessage,
  replaceMessage,
} from '../../store/messages-actions';
import { setDMSidebarOpen } from '../../store/ui-actions';
import {
  hasSeenOnboarding,
  saveHasSeenOnboarding,
  setOnboardingIndex,
  resetOnboarding,
  addToBlockList,
  isUserBlocked,
  setBlockedByList,
  addToBlockedByList,
  removeFromBlockedByList,
  isBlockedBy,
} from '../../utils/localstorage';
import {
  playNotificationSound,
  preloadNotificationSounds,
} from '../../utils/soundUtils';
import { extractMentionedUsers } from '../../utils/mentionParser';
import { isiOSDevice } from '../../constants/deviceType';
import MessagesContainer from './MessagesContainer';
import InputBar from './input/InputBar';
// import PhoneSignUpModal from './PhoneSignUpModal';
import SettingsMenu from './SettingsMenu';
import ButtonContainer from './ButtonContainer';
import ReachOutModal from './ReachOutModal';
import OnboardingModal from './OnboardingModal';
import 'react-toastify/dist/ReactToastify.css';
import '../room.css';
import { INCREMENT_ONBOARDING } from '../../store/onboarding-actions';
import CustomToastWithAction from './CustomToastWithAction';
import EditProfileModal from './EditProfileModal';
import ViewProfileModal from './ViewProfileModal';
import ColorFontModal from './ColorFontModal';
import AccountNumberModal from './AccountNumberModal';
import AccountSignInModal from './AccountSignInModal';
import ModBanModal from './ModBanModal';
import RoomSelectionModal from './RoomSelectionModal';
import OnboardingModalV2 from './OnboardingModalV2';
import OnboardingModalChatScape from './OnboardingModalChatScape';
import { isChatScapeMode } from '../../hooks/useSiteConfig';
// FriendRequestModal removed - integrated into DM sidebar
import AccountRequiredModal from './AccountRequiredModal';
import SignInOrUpModal from './SignInOrUpModal';
import CaptchaErrorModal from './CaptchaErrorModal';
import BlockListModal from './BlockListModal';
import VpnWarningModal from './VpnWarningModal';
import CaptchaVerificationModal from './CaptchaVerificationModal';
import DMSidebar from './dm/DMSidebar';
import DMHamburgerButton from './dm/DMHamburgerButton';
import ModReportsDashboard from './ModReportsDashboard';
import FeedbackModal from './FeedbackModal';
import NotificationManager from '../../services/NotificationManager';

const RoomContainer = styled.div`
  display: flex;
  overflow: hidden;
  height: 100%;
  width: 100%;
  background-color: var(--clr-bg);
  justify-content: space-between;
  flex-direction: column;
  transition: background-color 0.2s linear;
  position: relative;
`;

function RoomV2(props) {
  // const [modalIsOpen, setModalIsOpen] = useState(false);
  // const [phoneModalContext, setPhoneModalContext] = useState('');
  const [reachOutModalOpen, setReachOutModalOpen] = useState(false);
  const [onboardingModalOpen, setOnboardingModalOpen] = useState(false);
  const [settingsMenuIsOpen, setSettingsMenuIsOpen] = useState(false);
  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [viewProfileModalOpen, setViewProfileModalOpen] = useState(false);
  const [viewProfileUserId, setViewProfileUserId] = useState(null);
  const [colorFontModalOpen, setColorFontModalOpen] = useState(false);
  const [accountNumberModalOpen, setAccountNumberModalOpen] = useState(false);
  const [accountSignInModalOpen, setAccountSignInModalOpen] = useState(false);
  // Friend request modal removed - integrated into DM sidebar
  const [accountRequiredModalOpen, setAccountRequiredModalOpen] =
    useState(false);
  const [accountRequiredContext, setAccountRequiredContext] =
    useState('friends');
  const [signInOrUpModalOpen, setSignInOrUpModalOpen] = useState(false);
  const [captchaErrorModalOpen, setCaptchaErrorModalOpen] = useState(false);
  const [captchaErrorData, setCaptchaErrorData] = useState(null);
  const [blockListModalOpen, setBlockListModalOpen] = useState(false);
  const [vpnWarningModalOpen, setVpnWarningModalOpen] = useState(false);
  const [captchaVerificationModalOpen, setCaptchaVerificationModalOpen] =
    useState(false);
  const [pendingUserData, setPendingUserData] = useState(null);
  const [accountCreationContext, setAccountCreationContext] = useState('');
  const [accountSignInContext, setAccountSignInContext] = useState('');
  const [modReportsDashboardOpen, setModReportsDashboardOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  // Friend request count removed - now handled in DM sidebar
  const [nickname, setNickname] = useState('');
  const [isMod, setIsMod] = useState(false);
  const [isGlobalMod, setIsGlobalMod] = useState(false);
  const [modBanModalOpen, setModBanModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [useChatLayout, setUseChatLayout] = useState(() => {
    // Try to load saved chat layout preference from localStorage
    try {
      const savedLayout = localStorage.getItem('chatLayoutPreference');
      if (savedLayout !== null) {
        return savedLayout === 'true';
      }
    } catch (error) {
      console.error('Error loading chat layout preference:', error);
    }
    return true; // Default to chat layout
  });
  const [roomSelectionModalOpen, setRoomSelectionModalOpen] = useState(true);
  const [showOnboardingV2, setShowOnboardingV2] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(() => {
    // Try to load saved room from localStorage
    try {
      const savedRoom = localStorage.getItem('selectedRoom');
      if (savedRoom) {
        const room = JSON.parse(savedRoom);
        // If we have a saved room, don't show the modal on load
        setRoomSelectionModalOpen(false);
        return room;
      }
    } catch (error) {
      console.error('Error loading saved room:', error);
    }
    return null;
  });

  // Check if user has seen onboarding v2
  useEffect(() => {
    if (!selectedRoom && roomSelectionModalOpen) {
      const hasShownOnboarding = localStorage.getItem('hasShownOnboardingV2');
      if (!hasShownOnboarding) {
        setShowOnboardingV2(true);
        setRoomSelectionModalOpen(false);
      }
    }
  }, [selectedRoom, roomSelectionModalOpen]);

  // Preload notification sounds on component mount
  useEffect(() => {
    preloadNotificationSounds();
  }, []);

  // DM Panel state
  const [dmPanelOpen, setDmPanelOpen] = useState(false); // Show/hide DM overlay panel
  const [dmUnreadCount, setDmUnreadCount] = useState(0); // Total unread count (DM messages + friend requests)

  // Reply state
  const [replyToMessage, setReplyToMessage] = useState(null);

  // Subscribe to NotificationManager for count updates
  useEffect(() => {
    const unsubscribe = NotificationManager.subscribe((counts) => {
      setDmUnreadCount(counts.total);
    });

    return () => {
      unsubscribe();
    };
  }, []);


  const dispatch = useDispatch();

  const roomRef = useRef();
  const hasAlreadySignedIn = useRef(false);
  const socket = useRef(null);
  const messageQueue = useRef([]);
  const historicalMessages = useRef([]);
  const [connected, setConnected] = useState(-1);
  const [sendEnabled, setSendEnabled] = useState(true);
  const [replaying, setReplaying] = useState(false);
  const messagesSentCount = useRef(0);

  const onboardingInfo = useSelector((state) => state.onboarding);

  // Helper function to filter out messages that already exist in Redux state
  const filterNewMessages = (messages, getState) => {
    const currentReduxState = getState().messages.byId;
    return messages.filter((message) => {
      // First check by ID
      if (currentReduxState[message.id]) {
        return false;
      }

      // Also check if we have a message with same content (for self messages where userId differs)
      const existingMessages = Object.values(currentReduxState);
      const duplicate = existingMessages.find(
        (existing) => existing.messageText === message.messageText
      );

      return !duplicate;
    });
  };

  const handleVpnError = async (response) => {
    if (response.status === 403) {
      const errorData = await response.json();
      if (errorData.error === 'VPN_BLOCKED') {
        setVpnWarningModalOpen(true);
        return true; // Indicates VPN error was handled
      }
    }
    return false; // Not a VPN error
  };

  document.addEventListener('visibilitychange', function (e) {
    if (document.visibilityState === 'visible') {
      document.getElementById('favicon').href = 'favicon.ico';

      // Only use slow emission for classic view, not chat view
      if (!useChatLayout) {
        displayQueueMessage([...messageQueue.current], 2000);
      }
      messageQueue.current = [];
    }
  });

  function displayQueueMessage(curQueue, interval) {
    if (curQueue.length === 0) {
      setReplaying(false);
      return;
    }

    const message = curQueue.shift();
    if (isUserBlocked(message.userId) || isBlockedBy(message.userId)) {
      displayQueueMessage(curQueue, interval);
      return;
    }
    message.status = MESSAGE_STATUS_CACHED;
    dispatch(createMessage(message));
    setTimeout(function () {
      displayQueueMessage(curQueue, interval);
    }, interval);
  }

  async function registerUser(token) {
    try {
      const response = await fetch(API_URL + 'users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({
          push_notification_token: null,
        }),
      });
    } catch (err) {
      console.log('Error registering user', err);
    }
  }

  async function sendBlockedUserToServer(message) {
    const token = await getAuth().currentUser.getIdToken();
    let strippedMessage = {
      id: message.id,
      userId: message.userId,
      messageText: message.messageText,
      createdAt: message.createdAt,
    };
    try {
      const response = await fetch(API_URL + 'block', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({
          message: strippedMessage,
        }),
      });

      if (response.ok) {
        // API call succeeded, add user to local block list
        addToBlockList(message.userId, message.nickname, message.messageText);
        toast.success('User blocked successfully');
      }

    } catch (err) {
      console.log('Error blocking user:', err);
    }
  }

  useEffect(() => {
    const auth = getAuth();
    if (hasAlreadySignedIn.current === false && selectedRoom) {
      onAuthStateChanged(auth, (user) => {
        if (user == null) {
          signInAnonymously(auth)
            .then(async (result) => {
              // Signed in..
              registerUserAndConnectToSocket(result.user);
            })
            .catch((error) => {
              const errorCode = error.code;
              const errorMessage = error.message;
              // ...
            });
        } else {
          registerUserAndConnectToSocket(user);
        }
      });
    }
  }, [selectedRoom]);

  // Onboarding flow - starts after CAPTCHA completion
  const startOnboardingFlow = useCallback(() => {
    // Start the progression timer
    let interval = setInterval(() => {
      // Get fresh state by dispatching a thunk that can access current state
      dispatch((dispatch, getState) => {
        const currentOnboardingInfo = getState().onboarding;

        if (
          currentOnboardingInfo.needsMessage === false &&
          currentOnboardingInfo.index < 3
        ) {
          setOnboardingIndex(currentOnboardingInfo.index + 1);
          dispatch({ type: INCREMENT_ONBOARDING });
        } else if (currentOnboardingInfo.index >= 3) {
          // Mark onboarding as completed in localStorage to prevent it from running again
          saveHasSeenOnboarding();
          setOnboardingIndex(3); // Set to completion state
          // Deactivate onboarding when complete
          dispatch({ type: 'DEACTIVATE_ONBOARDING' });
          // Clear the interval since we're done
          clearInterval(interval);
        }
      });
    }, 8000);

    // Clean up after 60 seconds
    setTimeout(() => {
      clearInterval(interval);
      // Also deactivate if we timeout
      dispatch({ type: 'DEACTIVATE_ONBOARDING' });
    }, 60000);
  }, [dispatch]);

  // Handle body scroll based on chat layout for iOS compatibility
  useEffect(() => {
    if (!roomRef.current) return;

    if (useChatLayout && isiOSDevice()) {
      // Enable body scroll in chat layout on iOS devices for scrolling compatibility
      enableBodyScroll(roomRef.current);
    } else {
      // Disable body scroll in classic layout or on non-iOS devices (original behavior)
      disableBodyScroll(roomRef.current);
    }

    // Cleanup when component unmounts
    return () => {
      if (roomRef.current) {
        enableBodyScroll(roomRef.current);
      }
    };
  }, [useChatLayout]);

  // Handle Stripe success redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true' && urlParams.get('session_id')) {
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
      // Open ColorFontModal after successful subscription
      setTimeout(() => {
        setColorFontModalOpen(true);
      }, 1000);
    }
  }, []);

  // Expose onboarding reset function for testing
  useEffect(() => {
    window.resetOnboarding = () => {
      resetOnboarding();
      // Reset Redux state as well
      dispatch({ type: 'RESET_ONBOARDING', index: 0 });
      window.location.reload();
    };
    return () => {
      delete window.resetOnboarding;
    };
  }, [dispatch]);

  async function registerUserAndConnectToSocket(user) {
    if (hasAlreadySignedIn.current === false) {
      hasAlreadySignedIn.current = true;
      const token = await user.getIdToken();
      await registerUser(token);

      // Check if we already have a valid captcha session token
      const existingSessionToken = sessionStorage.getItem(
        'captcha_session_token'
      );

      if (existingSessionToken) {
        try {
          // Verify the token is still valid (basic check)
          const payload = JSON.parse(
            atob(existingSessionToken.split('.')[1])
          );
          const isExpired = Date.now() >= payload.exp * 1000;

          if (!isExpired) {
            connectToSocket(token, user);
            return;
          } else {
            sessionStorage.removeItem('captcha_session_token');
          }
        } catch (error) {
          sessionStorage.removeItem('captcha_session_token');
        }
      }

      // Store user data for after captcha verification
      setPendingUserData({ token, user });
      setCaptchaVerificationModalOpen(true);
    }
  }

  function addHistoricalMessage(message) {
    historicalMessages.current.push(message);
    while (historicalMessages.current.length > 10) {
      historicalMessages.current.shift();
    }
  }

  function connectToSocket(token, user) {
    var reconnect = false;
    if (socket.current != null) {
      socket.current.disconnect();
      reconnect = true;
    }

    // Prepare auth object with Firebase token
    const auth = {
      token: 'Bearer ' + token,
    };

    // Add session token for authentication
    const sessionToken = sessionStorage.getItem('captcha_session_token');
    if (sessionToken) {
      auth.sessionToken = sessionToken;
    } else {
      console.warn('Connecting without captcha session token - may fail');
    }

    socket.current = io(API_URL, {
      auth: auth,
      // Exponential backoff configuration
      reconnection: true,
      reconnectionAttempts: 10, // Max 10 attempts before giving up
      reconnectionDelay: 1000, // Start with 1 second delay
      reconnectionDelayMax: 30000, // Max 30 seconds between attempts
      randomizationFactor: 0.5, // Add randomization to prevent thundering herd
      timeout: 10000, // 10 second timeout for connection attempts
    });

    // Initialize NotificationManager with the socket
    NotificationManager.initialize(socket.current);

    socket.current.on('chat message', (message) => {
      if (isUserBlocked(message.userId)) {
        return;
      }
      // Don't show messages from users who have blocked us
      if (isBlockedBy(message.userId)) {
        return;
      }

      message.status = message.status || MESSAGE_STATUS_NEW;

      // Check if this message mentions or replies to the current user
      const currentUserId = getAuth().currentUser?.uid;
      const isFromCurrentUser =
        message.userId === currentUserId || message.userId === 'self';

      if (!isFromCurrentUser && currentUserId) {
        // Check if this is a reply to current user
        const isReplyToMe =
          message.replyToUserId === currentUserId ||
          message.replyToUserId === 'self';

        // Check if this message mentions current user and play sound if needed
        dispatch((dispatch, getState) => {
          const currentMessages = Object.values(getState().messages.byId || {});
          const mentionedUserIds = extractMentionedUsers(
            message.messageText || '',
            currentMessages
          );
          const isMentioned =
            mentionedUserIds.includes(currentUserId) ||
            mentionedUserIds.includes('self');

          // Play notification sound for mentions or replies
          if (isReplyToMe || isMentioned) {
            playNotificationSound(0.6);
          }

          return { type: 'NO_OP' }; // No-op action
        });
      }

      if (document.visibilityState !== 'visible') {
        document.getElementById('favicon').href = 'favicon-badged.ico';

        // Only queue messages if not in chat mode
        if (!useChatLayout) {
          messageQueue.current.push(message);
          if (messageQueue.current.length >= 10) {
            messageQueue.current.shift();
          }
        } else {
          // In chat mode, show messages immediately even when tab not visible
          dispatch(createMessage(message));
        }
      } else {
        dispatch(createMessage(message));
      }
      addHistoricalMessage(message);
    });

    socket.current.on('connect_error', async (err) => {
      setConnected((prev) => {
        return Math.max(prev - 0.5, -100);
      });
      console.log('Socket connect error:', err.message);

      if (err.message === 'user has not been registered') {
        await registerUser(token);
      }

      // You get disconnected for an old token, so refresh the token and reconnect
      const freshToken = await getAuth().currentUser.getIdToken();
      connectToSocket(freshToken, user);
    });

    socket.current.on('connect', async () => {
      setConnected(1);

      // Send browser fingerprint for bot detection
      try {
        const { generateFingerprint } = await import('../../utils/fingerprint');
        const fingerprint = await generateFingerprint();
        socket.current.emit('fingerprint', fingerprint);
      } catch (error) {
        console.error('Error generating fingerprint:', error);
      }

      // Load initial notification counts with random delay to prevent thundering herd
      const delay = Math.random() * 3000; // 0-3 seconds
      setTimeout(async () => {
        try {
          await NotificationManager.loadInitialCounts();
        } catch (error) {
          // Silently fail - this is not critical
        }
      }, delay);

      // Fetch subscription status
      try {
        const auth = getAuth();
        if (auth.currentUser) {
          const token = await auth.currentUser.getIdToken();
          const statusResponse = await fetch(API_URL + 'users/status', {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          });

          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            setHasSubscription(statusData.hasSubscription || false);
          }
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
        setHasSubscription(false);
      }

      // Fetch list of users who have blocked the current user
      try {
        const auth = getAuth();
        if (auth.currentUser) {
          const token = await auth.currentUser.getIdToken();
          const blockedByResponse = await fetch(API_URL + 'block/blocked-by', {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          });

          if (blockedByResponse.ok) {
            const blockedByData = await blockedByResponse.json();
            setBlockedByList(blockedByData.blockedByUserIds || []);
          }
        }
      } catch (error) {
        console.error('Error fetching blocked-by list:', error);
      }

      // Get current room from localStorage to avoid stale closure
      let currentRoom = null;
      try {
        const savedRoom = localStorage.getItem('selectedRoom');
        if (savedRoom) {
          currentRoom = JSON.parse(savedRoom);
        }
      } catch (error) {
        console.error('[RECONNECT] Error parsing localStorage room:', error);
      }

      // Load messages for the current room
      if (currentRoom) {
        // Tell server which room we're in so it can track our state
        socket.current.emit('language', currentRoom);

        try {
          const result = await fetch(
            API_URL + `messages?language=${currentRoom.id}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
              },
            }
          );

          // Check for VPN error first
          if (await handleVpnError(result)) {
            return; // VPN error handled, stop processing
          }

          const historicalMessagesFromBackend = await result.json();
          const allMessages = historicalMessagesFromBackend.messages || [];

          // Use dispatch to get fresh state and filter messages
          let newMessages = [];
          let currentReduxState = {};

          dispatch((dispatch, getState) => {
            currentReduxState = getState().messages.byId;
            newMessages = filterNewMessages(allMessages, getState);

            return { newMessages, currentReduxState };
          });

          if (useChatLayout) {
            // In chat mode, display messages immediately
            newMessages.forEach((message) => {
              if (!isUserBlocked(message.userId) && !isBlockedBy(message.userId)) {
                message.status = MESSAGE_STATUS_CACHED;
                dispatch(createMessage(message));
              }
            });
          } else {
            // In classic mode, use the legacy approach (combine new with existing)
            const existingHistorical = historicalMessages.current || [];
            const combinedMessages = [...existingHistorical];

            // Add new messages that don't already exist in historical messages
            newMessages.forEach((newMessage) => {
              const existsInHistorical = existingHistorical.find(
                (msg) => msg.id === newMessage.id
              );
              if (!existsInHistorical) {
                combinedMessages.push(newMessage);
              }
            });

            historicalMessages.current = combinedMessages;
            onClickReplay(false);
          }
        } catch (error) {
          console.error('Error loading messages on connect:', error);
        }
      }
      // If no selectedRoom, the user will see the room selection modal
    });

    socket.current.on('modStatus', (data) => {
      setIsMod(data.isMod);
      setIsGlobalMod(data.isGlobalMod || false);
    });

    socket.current.on('warning', (warning) => {
      //setBanWarning(warning);
      if (warning.type === 'RATE_LIMITED') {
        setSendEnabled(false);
        setTimeout(() => {
          setSendEnabled(true);
        }, warning.time * 950); // Enable 5% sooner than the actual time
      }
    });

    socket.current.on('status', async (status) => {
      if (status === 'NOT_VERIFIED') {
        // setModalIsOpen(true);
      }
    });

    socket.current.on('captcha_error', (errorData) => {
      setCaptchaErrorData(errorData);
      setCaptchaErrorModalOpen(true);
    });

    // Notification handlers are now managed by NotificationManager
    // Old handlers removed to prevent duplicate processing

    socket.current.on('mention_notification', (notification) => {
      // TODO: Handle mention notifications (could add badge, sound, etc.)
    });

    socket.current.on('user_banned', (banData) => {
      const { bannedUserNickname, modNickname, duration } = banData;

      toast.warning(
        `${bannedUserNickname || 'A user'} was banned by ${
          modNickname || 'a moderator'
        } for ${duration} day${duration !== 1 ? 's' : ''}`,
        {
          autoClose: 8000,
          hideProgressBar: false,
        }
      );
    });

    socket.current.on('delete', (deleteData) => {
      if (deleteData.messageIds && Array.isArray(deleteData.messageIds)) {
        deleteData.messageIds.forEach((messageId) => {
          dispatch(deleteMessage(messageId));
        });
      }
    });

    // Listen for when another user blocks us
    socket.current.on('blocked_by_user', ({ userId }) => {
      addToBlockedByList(userId);
    });

    // Listen for when another user unblocks us
    socket.current.on('unblocked_by_user', ({ userId }) => {
      removeFromBlockedByList(userId);
    });

    // Reaction event listeners
    socket.current.on('reaction_added', (data) => {
      const { messageId, reactionType, count, userId } = data;
      dispatch(addReactionToMessage(messageId, reactionType, count, userId));
    });

    socket.current.on('reaction_removed', (data) => {
      const { messageId, reactionType, count, userId } = data;
      dispatch(
        removeReactionFromMessage(messageId, reactionType, count, userId)
      );
    });

    socket.current.on('reaction_error', (error) => {
      console.warn('Reaction error:', error);
      // Could show a toast notification here
    });

    // Handle message ID updates for our own messages
    socket.current.on('update_message_id', (data) => {
      const { tempId, realId, message } = data;
      // Skip replacement in classic view to avoid repositioning issues
      if (!useChatLayout) {
        return;
      }
      // Replace the temporary message with the real one from server
      dispatch(replaceMessage(tempId, message));
    });
  }

  function sendMessage(messageData, token) {
    // Handle both old string format and new object format
    let messageText,
      replyToId,
      replyToText,
      replyToNickname,
      replyToUserId,
      replyToColor;
    if (typeof messageData === 'string') {
      messageText = messageData;
      replyToId = null;
      replyToText = null;
      replyToNickname = null;
      replyToUserId = null;
      replyToColor = null;
    } else {
      messageText = messageData.messageText;
      replyToId = messageData.replyToId;
      replyToText = messageData.replyToText;
      replyToNickname = messageData.replyToNickname;
      replyToUserId = messageData.replyToUserId;
      replyToColor = messageData.replyToColor;
    }

    let message = {
      id: uuidv4(),
      messageText: messageText,
      status: MESSAGE_STATUS_NEW,
      token: token,
      replyToId: replyToId,
      replyToText: replyToText,
      replyToNickname: replyToNickname,
      replyToUserId: replyToUserId,
      replyToColor: replyToColor,
    };

    socket.current.emit('chat message', message);
    const analytics = getAnalytics();
    logEvent(analytics, 'sent_message', {
      messageLength: messageText.length,
      messageType: 'public',
    });

    messagesSentCount.current++;
    if (messagesSentCount.current == 5) {
      toast.info(
        <CustomToastWithAction
          text='Have ideas for VentScape? Click here to leave feedback and let me know!'
          onClick={() => {
            setFeedbackModalOpen(true);
            toast.dismiss(); // Close the toast when clicked
          }}
        />,
        {
          position: 'top-center',
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'dark',
          onClose: () => {},
        }
      );
    }

    if (messagesSentCount.current == 10) {
      toast.info(
        <CustomToastWithAction
          text='Keep VentScape running! Click here to subscribe and support the platform'
          onClick={() => {
            setColorFontModalOpen(true);
            toast.dismiss(); // Close the toast when clicked
          }}
        />,
        {
          position: 'top-center',
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'dark',
          onClose: () => {},
        }
      );
    }
  }

  // async function phoneNumberReceived() {
  //   const token = await getAuth().currentUser.getIdToken();
  //   await registerUser(token);
  //   connectToSocket(token, getAuth().currentUser);

  //   // If phone verification was for subscription, reopen color font modal
  //   if (phoneModalContext === 'subscription') {
  //     setColorFontModalOpen(true);
  //   }
  // }

  function accountNumberGenerated(accountNumber) {
    setAccountNumberModalOpen(false);
    // Only proceed to subscription if not from friends context
    if (accountCreationContext !== 'friends') {
      // After account number is generated and confirmed, proceed to subscription
      // The ColorFontModal will refresh user status when it opens
      setColorFontModalOpen(true);
    }
    // Clear the context
    setAccountCreationContext('');
  }

  function accountNumberLinked(accountNumber) {
    setAccountSignInModalOpen(false);
    // Only proceed to subscription if not from settings menu
    if (accountSignInContext !== 'settings') {
      // After account number is linked (data transferred), proceed to subscription
      // The ColorFontModal will refresh user status when it opens and see the account
      setColorFontModalOpen(true);
    }
    // Clear the context
    setAccountSignInContext('');
  }

  function getLanguage() {
    // If room is selected, use room's language, otherwise fall back to browser language
    if (selectedRoom) {
      return selectedRoom.language;
    }
    var language = navigator.language || navigator.userLanguage || 'en';
    if (language.includes('en')) {
      language = 'en';
    }
    return language;
  }

  function getRoomId() {
    // Return full room ID if available, otherwise fall back to language
    if (selectedRoom) {
      return selectedRoom.id;
    }
    return getLanguage();
  }

  async function handleRoomSelect(room) {
    const previousRoom = selectedRoom;

    // Prevent selecting the same room
    if (previousRoom && previousRoom.id === room.id) {
      setRoomSelectionModalOpen(false);
      return; // Early return - don't process the same room
    }

    setSelectedRoom(room);
    setRoomSelectionModalOpen(false);

    // Store selected room in localStorage for persistence
    localStorage.setItem('selectedRoom', JSON.stringify(room));

    // If we're switching rooms and already connected, handle the switch
    if (previousRoom && socket.current && socket.current.connected) {
      // Clear messages when switching rooms to avoid confusion
      dispatch(clearMessages());
      messageQueue.current = [];
      historicalMessages.current = [];

      // Send new room to socket - backend will handle leaving old room and joining new
      socket.current.emit('language', room);
    }

    // Load messages for the room (both initial connect and room switch)
    if (socket.current && socket.current.connected) {
      try {
        const token = await getAuth().currentUser.getIdToken();
        const result = await fetch(API_URL + `messages?language=${room.id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
        });

        // Check for VPN error first
        if (await handleVpnError(result)) {
          return; // VPN error handled, stop processing
        }

        const historicalMessagesFromBackend = await result.json();
        const allMessages = historicalMessagesFromBackend.messages || [];

        // Use dispatch to get fresh state and filter messages
        let newMessages = [];
        let currentReduxState = {};

        dispatch((dispatch, getState) => {
          currentReduxState = getState().messages.byId;
          newMessages = filterNewMessages(allMessages, getState);

          return { newMessages, currentReduxState };
        });

        if (useChatLayout) {
          // In chat mode, display messages immediately
          newMessages.forEach((message) => {
            if (!isUserBlocked(message.userId) && !isBlockedBy(message.userId)) {
              message.status = MESSAGE_STATUS_CACHED;
              dispatch(createMessage(message));
            }
          });
        } else {
          // In classic mode, use the legacy approach
          historicalMessages.current = allMessages; // For room switch, use all messages since we cleared before
          onClickReplay(false);
        }
      } catch (error) {
        console.error('Error loading messages for room:', error);
        Sentry.captureException(error, {
          tags: {
            component: 'RoomV2',
            action: 'handleRoomSelect',
            room: room?.id || 'unknown',
          },
          extra: {
            roomData: room,
            previousRoom: previousRoom?.id || 'none',
          },
        });
        toast.error('Failed to load messages. Please refresh and try again.');
      }
    }
  }

  function handleModBanClick(message) {
    setSelectedMessage(message);
    setModBanModalOpen(true);
  }

  // Reaction handlers
  function handleAddReaction(messageId, reactionType) {
    if (socket.current && socket.current.connected) {
      socket.current.emit('add_reaction', { messageId, reactionType });
    }
  }

  function handleRemoveReaction(messageId, reactionType) {
  
    if (socket.current && socket.current.connected) {
      socket.current.emit('remove_reaction', { messageId, reactionType });
    }
  }

  function handleReactionCountClick(reactionType) {
    // Future: Show modal with users who reacted
  }

  function handleReplyClick(message) {
    // Clear first to ensure state change is detected, then set the message
    setReplyToMessage(null);
    setTimeout(() => {
      setReplyToMessage(message);
    }, 0);
  }

  function toggleChatLayout() {
    const newChatLayout = !useChatLayout;
    setUseChatLayout(newChatLayout);

    // Save preference to localStorage
    try {
      localStorage.setItem('chatLayoutPreference', newChatLayout.toString());
    } catch (error) {
      console.error('Error saving chat layout preference:', error);
    }

    // If switching to chat layout, stop any ongoing replay and show remaining historical messages instantly
    if (newChatLayout && replaying) {
      setReplaying(false);

      // Instantly dispatch any remaining historical messages that haven't been replayed yet
      if (historicalMessages.current && historicalMessages.current.length > 0) {
        historicalMessages.current.forEach((message) => {
          message.status = MESSAGE_STATUS_CACHED;
          dispatch(createMessage(message));
        });
      }
    }

    // If switching to chat layout, instantly dispatch all queued messages in correct order
    if (newChatLayout && messageQueue.current.length > 0) {
      const queuedMessages = [...messageQueue.current];
      messageQueue.current = [];

      // Dispatch messages sequentially with small delays to ensure correct ordering
      queuedMessages.forEach((message, index) => {
        setTimeout(() => {
          dispatch(createMessage(message));
        }, index * 10); // 10ms delay between each message
      });
    }
  }

  function onClickReplay(realClick = true) {
    if (!replaying) {
      setReplaying(true);

      // If in chat layout, display all messages instantly
      if (useChatLayout) {
        const messages = [...(historicalMessages.current || [])];
        messages.forEach((message) => {
          if (!isUserBlocked(message.userId) && !isBlockedBy(message.userId)) {
            message.status = MESSAGE_STATUS_CACHED;
            dispatch(createMessage(message));
          }
        });
        setReplaying(false);
      } else {
        // Only use slow emission for classic view
        displayQueueMessage([...(historicalMessages.current || [])], 2000);
      }

      messageQueue.current = [];
    }

    if (realClick) {
      const analytics = getAnalytics();
      logEvent(analytics, 'pressed_replay');
    }
  }

  // Friend request count fetching removed - now handled in DM sidebar

  // Friend request count tracking removed - now handled in DM sidebar
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // Friend request tracking removed
    });

    return unsubscribe;
  }, []);

  // DM message handling moved to NotificationManager


  // Friend request handlers
  // Friend notifications removed - integrated into DM sidebar

  // Friend request success handler removed - now handled in DM sidebar

  const handleRequireAccount = (context = 'friends') => {
    // Friend request modal removed - integrated into DM sidebar
    setAccountRequiredContext(context);
    setAccountRequiredModalOpen(true);
  };

  const handleCreateAccount = () => {
    setAccountRequiredModalOpen(false);
    setAccountCreationContext('friends'); // Track that this came from friends
    setAccountNumberModalOpen(true);
  };

  const handleGoogleSignIn = async () => {
    try {
      setSignInOrUpModalOpen(false);

      const user = await signInWithGoogle();
    

      // Success! User is now signed in with their Google account
      toast.success('Successfully signed in with Google!');
    } catch (error) {
      console.error('Google sign-in failed:', error);
      toast.error(`Google sign-in failed: ${error.message}`);
    }
  };

  const handleSendFriendRequest = async (receiverId) => {
    try {

      // Validation
      if (
        !receiverId ||
        receiverId === 'undefined' ||
        receiverId === 'null' ||
        receiverId.toString() === 'NaN'
      ) {
        console.error('Invalid receiverId:', receiverId);
        toast.error('Invalid user ID. Please try again.');
        return;
      }

      const auth = getAuth();
      if (!auth.currentUser) {
        toast.error('Please sign in to send friend requests');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + 'friends/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({
          receiver_id: receiverId,
        }),
      });

      if (response.ok) {
        const analytics = getAnalytics();
        logEvent(analytics, 'friend_request_sent_from_message');
        toast.success('Friend request sent successfully!');
      } else {
        const errorData = await response.json();
        if (errorData.requireAccount) {
          handleRequireAccount('send_request');
        } else {
          toast.error(errorData.error || 'Failed to send friend request');
        }
      }
    } catch (error) {
      console.error('Error sending friend request:', error);
      toast.error('Failed to send friend request');
    }
  };

  const handleCaptchaVerificationSuccess = (sessionToken) => {
    setCaptchaVerificationModalOpen(false);

    if (pendingUserData) {
      const { token, user } = pendingUserData;
      setPendingUserData(null);
      connectToSocket(token, user);
    }

    // Start onboarding tooltips after user has completed CAPTCHA and entered the room
    let shouldStartOnboarding = false;

    // For new users, reset the tooltip onboarding to start from the beginning
    if (!hasSeenOnboarding()) {
      setOnboardingIndex(0);
      dispatch({ type: 'RESET_ONBOARDING', index: 0 });
      shouldStartOnboarding = true;
    } else if (onboardingInfo.index < 3) {
      // For returning users, only start if they haven't completed tooltips yet
      shouldStartOnboarding = true;
    }

    if (shouldStartOnboarding) {
      // Activate onboarding first
      dispatch({ type: 'ACTIVATE_ONBOARDING' });

      setTimeout(() => {
        startOnboardingFlow();
      }, 2000); // Give a moment for the UI to settle
    }
  };

  // DM Panel handlers
  const toggleDMPanel = () => {
    setDmPanelOpen((prev) => {
      const newState = !prev;
      dispatch(setDMSidebarOpen(newState));
      return newState;
    });
    const analytics = getAnalytics();
    logEvent(analytics, dmPanelOpen ? 'dm_panel_closed' : 'dm_panel_opened');
  };

  const closeDMPanel = () => {
    setDmPanelOpen(false);
    dispatch(setDMSidebarOpen(false));
  };

  const handleViewProfile = (userId) => {
    setViewProfileUserId(userId);
    setViewProfileModalOpen(true);
  };

  const handleCloseViewProfileModal = () => {
    setViewProfileModalOpen(false);
    setViewProfileUserId(null);
  };

  const handleConnectionClick = () => {
    
    // Check if we have a valid session token
    const sessionToken = sessionStorage.getItem('captcha_session_token');
    
    if (!sessionToken) {
      
      // If we have pending user data, use it; otherwise get current auth
      if (!pendingUserData && hasAlreadySignedIn.current) {
        const auth = getAuth();
        if (auth.currentUser) {
          auth.currentUser.getIdToken().then(token => {
            setPendingUserData({ token, user: auth.currentUser });
            setCaptchaVerificationModalOpen(true);
          });
        }
      } else {
        setCaptchaVerificationModalOpen(true);
      }
    } else {
      // Try to parse and check if token is expired
      try {
        const payload = JSON.parse(atob(sessionToken.split('.')[1]));
        const isExpired = Date.now() >= payload.exp * 1000;
        
        if (isExpired) {
          sessionStorage.removeItem('captcha_session_token');
          
          const auth = getAuth();
          if (auth.currentUser) {
            auth.currentUser.getIdToken().then(token => {
              setPendingUserData({ token, user: auth.currentUser });
              setCaptchaVerificationModalOpen(true);
            });
          }
        } else {
          // Token is valid, try to reconnect
          const auth = getAuth();
          if (auth.currentUser) {
            auth.currentUser.getIdToken().then(token => {
              connectToSocket(token, auth.currentUser);
            });
          }
        }
      } catch (error) {
        sessionStorage.removeItem('captcha_session_token');
        setCaptchaVerificationModalOpen(true);
      }
    }
  };

  return (
    <GoogleReCaptchaProvider reCaptchaKey='6LegcT8aAAAAAF0lGhv70wvPtH2l8fOGI_3tAZwI'>
      <RoomContainer ref={roomRef}>
        <ToastContainer
          position='top-center'
          autoClose={10000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme='dark'
        />

        <MessagesContainer
          sendBlockedUserToServer={sendBlockedUserToServer}
          isMod={isMod}
          onModBanClick={handleModBanClick}
          onFriendRequestClick={handleSendFriendRequest}
          onViewProfileClick={handleViewProfile}
          onReplyClick={handleReplyClick}
          onAddReaction={handleAddReaction}
          onRemoveReaction={handleRemoveReaction}
          onReactionCountClick={handleReactionCountClick}
          currentUserId={getAuth().currentUser?.uid}
          hasSubscription={hasSubscription}
          useChatLayout={useChatLayout}
          room={selectedRoom?.id || 'en-chat'}
        />
        <ButtonContainer
          replaying={replaying}
          useChatLayout={useChatLayout}
          onClickSettings={() => {
            const nextOpenState = !settingsMenuIsOpen;
            setSettingsMenuIsOpen((prevState) => !prevState);
            const analytics = getAnalytics();
            logEvent(analytics, 'pressed_settings', {
              open: nextOpenState,
            });
          }}
          onClickReplay={onClickReplay}
          connected={connected}
          onConnectionClick={handleConnectionClick}
          onSwitchRoom={() => {
            setRoomSelectionModalOpen(true);
          }}
          onClickArchive={() => {
            window.open('/archive', '_blank');
            const analytics = getAnalytics();
            logEvent(analytics, 'archive_opened');
          }}
          isMod={isMod}
          onClickReports={() => {
            setModReportsDashboardOpen(true);
          }}
          // Friends button removed - integrated into DM sidebar
          currentRoom={selectedRoom}
        />
        {/* <PhoneSignUpModal
          visible={modalIsOpen}
          context={phoneModalContext}
          onClose={() => {
            setModalIsOpen(false);
            setPhoneModalContext('');
          }}
          onSuccess={phoneNumberReceived}
        ></PhoneSignUpModal> */}

        <ReachOutModal
          visible={reachOutModalOpen}
          onClose={() => {
            setReachOutModalOpen(false);
          }}
        />
        <OnboardingModal
          visible={onboardingModalOpen}
          onClose={() => {
            setOnboardingModalOpen(false);
          }}
        />
        <SettingsMenu
          visible={settingsMenuIsOpen}
          openInstructions={() => {
            setOnboardingModalOpen(true);
            setSettingsMenuIsOpen(false);
          }}
          openEditProfile={() => {
            setEditProfileModalOpen(true);
            setSettingsMenuIsOpen(false);
          }}
          openPhoneSignup={(context = '') => {
            // setPhoneModalContext(context);
            // setModalIsOpen(true);
            setSettingsMenuIsOpen(false);
          }}
          openColorFont={() => {
            setColorFontModalOpen(true);
            setSettingsMenuIsOpen(false);
          }}
          openSignInOrUp={() => {
            setSignInOrUpModalOpen(true);
            setSettingsMenuIsOpen(false);
          }}
          openAccountSignIn={() => {
            setAccountSignInContext('settings'); // Track that this came from settings menu
            setAccountSignInModalOpen(true);
            setSettingsMenuIsOpen(false);
          }}
          openBlockList={() => {
            setBlockListModalOpen(true);
            setSettingsMenuIsOpen(false);
          }}
          closeSettingsMenu={() => {
            setSettingsMenuIsOpen(false);
          }}
          useChatLayout={useChatLayout}
          onToggleChatLayout={toggleChatLayout}
        ></SettingsMenu>
        <EditProfileModal
          visible={editProfileModalOpen}
          onClose={() => {
            setEditProfileModalOpen(false);
          }}
          onProfileUpdate={(profileData) => {
            setNickname(profileData.nickname || '');
            // Store other profile data if needed
          }}
          currentProfile={{
            nickname: nickname,
            // Will be loaded from API in the modal
          }}
        />
        <ViewProfileModal
          visible={viewProfileModalOpen}
          onClose={handleCloseViewProfileModal}
          userId={viewProfileUserId}
        />
        <ColorFontModal
          visible={colorFontModalOpen}
          onClose={() => {
            setColorFontModalOpen(false);
          }}
          openAccountNumber={() => {
            setAccountNumberModalOpen(true);
            setColorFontModalOpen(false);
          }}
          openAccountSignIn={() => {
            setAccountSignInModalOpen(true);
            setColorFontModalOpen(false);
          }}
        />
        <AccountNumberModal
          visible={accountNumberModalOpen}
          onClose={() => {
            setAccountNumberModalOpen(false);
          }}
          onComplete={accountNumberGenerated}
        />
        <AccountSignInModal
          visible={accountSignInModalOpen}
          onClose={() => {
            setAccountSignInModalOpen(false);
          }}
          onSuccess={accountNumberLinked}
          onSocketReconnect={connectToSocket}
        />
        <ModBanModal
          isOpen={modBanModalOpen}
          onClose={() => {
            setModBanModalOpen(false);
            setSelectedMessage(null);
          }}
          message={selectedMessage}
          room={selectedRoom?.id || 'en-chat'}
        />
        <RoomSelectionModal
          visible={roomSelectionModalOpen && !showOnboardingV2}
          onClose={selectedRoom ? () => setRoomSelectionModalOpen(false) : null}
          onRoomSelect={handleRoomSelect}
          currentRoom={selectedRoom}
          allowClose={!!selectedRoom}
        />
        {isChatScapeMode() ? (
          <OnboardingModalChatScape
            visible={showOnboardingV2}
            onClose={() => {
              setShowOnboardingV2(false);
              localStorage.setItem('hasShownOnboardingV2', 'true');
              // If they didn't select a room, show the room selection modal
              if (!selectedRoom) {
                setRoomSelectionModalOpen(true);
              }
            }}
            onRoomSelect={(room) => {
              // Set the localStorage flag first
              localStorage.setItem('hasShownOnboardingV2', 'true');
              // Select the room (this will close the modal via the selectedRoom state)
              handleRoomSelect(room);
              // Close the onboarding modal after room is selected
              setShowOnboardingV2(false);
            }}
          />
        ) : (
          <OnboardingModalV2
            visible={showOnboardingV2}
            onClose={() => {
              setShowOnboardingV2(false);
              localStorage.setItem('hasShownOnboardingV2', 'true');
              // If they didn't select a room, show the room selection modal
              if (!selectedRoom) {
                setRoomSelectionModalOpen(true);
              }
            }}
            onRoomSelect={(room) => {
              // Set the localStorage flag first
              localStorage.setItem('hasShownOnboardingV2', 'true');
              // Select the room (this will close the modal via the selectedRoom state)
              handleRoomSelect(room);
              // Close the onboarding modal after room is selected
              setShowOnboardingV2(false);
            }}
          />
        )}
        {/* FriendRequestModal removed - integrated into DM sidebar */}
        <AccountRequiredModal
          visible={accountRequiredModalOpen}
          onClose={() => setAccountRequiredModalOpen(false)}
          onCreateAccount={handleCreateAccount}
          context={accountRequiredContext}
        />
        <SignInOrUpModal
          visible={signInOrUpModalOpen}
          onClose={() => setSignInOrUpModalOpen(false)}
          onGoogleSignIn={handleGoogleSignIn}
          onCreateAccount={() => {
            setSignInOrUpModalOpen(false);
            setAccountRequiredContext('friends');
            setAccountRequiredModalOpen(true);
          }}
          onSignIn={() => {
            setSignInOrUpModalOpen(false);
            setAccountSignInContext('settings');
            setAccountSignInModalOpen(true);
          }}
        />
        <CaptchaErrorModal
          visible={captchaErrorModalOpen}
          onClose={() => {
            setCaptchaErrorModalOpen(false);
            setCaptchaErrorData(null);
          }}
          onRefresh={() => window.location.reload()}
          errorData={captchaErrorData}
        />
        <BlockListModal
          visible={blockListModalOpen}
          onClose={() => setBlockListModalOpen(false)}
        />
        <VpnWarningModal
          visible={vpnWarningModalOpen}
          onClose={() => setVpnWarningModalOpen(false)}
        />
        <CaptchaVerificationModal
          visible={captchaVerificationModalOpen}
          onSuccess={handleCaptchaVerificationSuccess}
        />
        <ModReportsDashboard
          isOpen={modReportsDashboardOpen}
          onClose={() => setModReportsDashboardOpen(false)}
          room={isGlobalMod ? 'global' : selectedRoom?.id || 'en-chat'}
          isGlobalMod={isGlobalMod}
        />
        <FeedbackModal
          visible={feedbackModalOpen}
          onClose={() => setFeedbackModalOpen(false)}
        />

        <InputBar
          sendMessage={sendMessage}
          enabled={sendEnabled}
          useChatLayout={useChatLayout}
          replyToMessage={replyToMessage}
          hasSubscription={hasSubscription}
        />

        {/* DM Hamburger Button */}
        <DMHamburgerButton
          onClick={toggleDMPanel}
          unreadCount={dmUnreadCount}
        />

        {/* DM Sidebar Overlay */}
        <DMSidebar
          isOpen={dmPanelOpen}
          onClose={closeDMPanel}
          socket={socket.current}
          onUnreadCountChange={setDmUnreadCount}
          onViewProfile={handleViewProfile}
        />
      </RoomContainer>
      {/* <HighlightedComponents />
      <DisplayRandomMessages /> */}
    </GoogleReCaptchaProvider>
  );
}

export default RoomV2;
